import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { AbstractAuthService, AbstractDiffusionSessionService } from '@zero/shared/util-abstract-services';
import { Quote } from '@zero/shared/util-models';
import { pollingFallbackInterval } from 'constants/shared-constants';
import { datatypes, Session, TopicSpecification, ValueStream } from 'diffusion';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, interval, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { buffer, debounceTime, filter, map, retry, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PushQuoteService implements OnDestroy {
  private readonly diffusionSessionService = inject(AbstractDiffusionSessionService);
  private readonly httpClient = inject(HttpClient);
  private readonly authService = inject(AbstractAuthService);

  private readonly deleteSubjectWithoutSubscriptAfter = 30; // seconds

  // For book keeping of registered quote subjects
  private isins: Record<
    string,
    {
      quoteSubject: Subject<Quote>;
      noObserversPresentTimestamp?: number;
    }
  > = {};

  // To task new un/subscriptions for isins
  private isinsToSubscribe = new Subject<string>();
  private isinsToUnsubscribe = new Subject<string>();

  private valStream: ValueStream;

  private subscribeToDiffusionTaskSubscription: Subscription;
  private unsubscribeFromDiffusionTaskSubscription: Subscription;
  private subscribeToPollingTaskSubscription: Subscription;

  private pollingTimer: Observable<number>;
  private pollingTimerSubscription: Subscription;

  private observablesToUnsubcribe: Subscription[] = [];

  private isLoggedIn: boolean;

  private initialPollingTrigger$ = new Subject<void>();

  private static createQuoteFromJsonValue(value: any, isin: string): Quote {
    const q: Quote = new Quote();
    if (value.a !== 'N/A') {
      q.ask = value.a;
    }
    if (value.b !== 'N/A') {
      q.bid = value.b;
    }
    if (value.av !== 'N/A') {
      q.askv = value.av;
    }
    if (value.bv !== 'N/A') {
      q.bidv = value.bv;
    }
    q.time = new Date(value.t).toISOString();
    q.isin = isin;
    return q;
  }

  private static createQuoteFromDiffusionValue(value: any, isin: string): Quote {
    return PushQuoteService.createQuoteFromJsonValue(value.get(), isin);
  }

  public static createQuoteBufferFn(isinsAmount: number, extreme?: boolean): Observable<number> {
    // Konstruktion der Buffer-Funktion:
    // Für einen anfänglichen Zeitraum soll nur ganz kurz gepuffert werden (für die Dauer von 1x bufferDuration),
    // so dass die ersten Kurse sehr schnell sichtbar werden.
    // Danach soll dann immer für bufferDuration gepuffert werden.
    const bufferDuration = PushQuoteService.calcBufferTime(isinsAmount, extreme);
    const initialShortTimeBufferingDuration = 250;
    const initialShortTimeBufferingFlushBufferCount =
      bufferDuration / initialShortTimeBufferingDuration > 1 ? bufferDuration / initialShortTimeBufferingDuration : 1;
    const initialShortTimeBufferingInterval = interval(initialShortTimeBufferingDuration).pipe(
      take(initialShortTimeBufferingFlushBufferCount),
    );
    const flushBufferInterval = interval(bufferDuration);
    return merge(initialShortTimeBufferingInterval, flushBufferInterval);
  }

  public static calcBufferTime(count: number, extreme?: boolean): number {
    if (extreme) {
      if (count < 101) {
        return 250;
      } else if (count < 251) {
        return 4000;
      } else if (count < 501) {
        return 6000;
      } else {
        return 7500;
      }
    } else if (count < 101) {
      return 250;
    } else if (count < 251) {
      return 1000;
    } else if (count < 501) {
      return 2500;
    } else {
      return 5000;
    }
  }

  constructor() {
    this.pollingTimer = timer(0, pollingFallbackInterval);
    this.observablesToUnsubcribe.push(this.handleDiffusionSession());
    this.observablesToUnsubcribe.push(this.handleAuthState());
    this.observablesToUnsubcribe.push(this.handleQuotePolling());
    this.observablesToUnsubcribe.push(this.handlePushUnsubscribe());
  }

  ngOnDestroy(): void {
    this.observablesToUnsubcribe.forEach((elem) => elem.unsubscribe());
  }

  private handleAuthState(): Subscription {
    return this.authService.isLoggedIn$().subscribe((loggedInCustomer) => {
      this.isLoggedIn = loggedInCustomer;
      if (!loggedInCustomer) {
        // stop polling in case we were
        if (this.pollingTimerSubscription) {
          this.pollingTimerSubscription.unsubscribe();
        }
        if (this.subscribeToPollingTaskSubscription) {
          this.subscribeToPollingTaskSubscription.unsubscribe();
        }
        this.isins = {};
      }
    });
  }

  private handleDiffusionSession(): Subscription {
    return this.diffusionSessionService.diffusionSession$.subscribe((diffusionSession) => {
      if (diffusionSession) {
        this.valStream = diffusionSession.addStream('?wp//', datatypes.json());
        this.valStream.on('value', this.onValueFromDiffusion.bind(this));
        this.valStream.on('error', () => {
          if (diffusionSession?.isConnected()) {
            // TODO: error handling
          }
        });

        this.subscribeToDiffusionTaskSubscription = this.isinsToSubscribe.subscribe((isin) => {
          this.subscribeFromDiffusion(diffusionSession, isin);
        });

        this.unsubscribeFromDiffusionTaskSubscription = this.isinsToUnsubscribe.subscribe((isin) => {
          this.unsubscribeFromDiffusion(diffusionSession, isin);
        });

        Object.keys(this.isins).forEach((isin) => this.subscribeFromDiffusion(diffusionSession, isin));

        // cancel polling related subscriptions
        if (this.pollingTimerSubscription) {
          this.pollingTimerSubscription.unsubscribe();
        }
        if (this.subscribeToPollingTaskSubscription) {
          this.subscribeToPollingTaskSubscription.unsubscribe();
        }
      } else {
        if (this.valStream) {
          this.valStream.close();
        }
        if (this.subscribeToDiffusionTaskSubscription) {
          this.subscribeToDiffusionTaskSubscription.unsubscribe();
        }
        if (this.unsubscribeFromDiffusionTaskSubscription) {
          this.unsubscribeFromDiffusionTaskSubscription.unsubscribe();
        }
        if (!this.pollingTimerSubscription && this.isLoggedIn) {
          this.pollingTimerSubscription = this.pollingTimer.subscribe(() => {
            // Initial quote poll on page load when no diffusion session exists
            this.pollQuotes();
          });
          this.subscribeToPollingTaskSubscription = this.isinsToSubscribe.subscribe(() => {
            // We don't poll quotes for every single ISIN!
            // this.pollQuote(isin);
          });

          // We poll quotes once per Diffusion reconnect for all ISINs in a single POST
          this.pollQuotes();
        }
      }
    });
  }

  private handleQuotePolling(): Subscription {
    return this.initialPollingTrigger$.pipe(debounceTime(100)).subscribe(() => {
      this.pollQuotes();
    });
  }

  private handlePushUnsubscribe(): Subscription {
    return timer(0, 5000).subscribe(() => {
      const isinsToUnsubscribe = Object.keys(this.isins).filter((isin) => {
        const subscriptionItem = this.isins[isin];
        const noObservers = subscriptionItem.quoteSubject.observers.length === 0;
        if (noObservers) {
          if (!subscriptionItem.noObserversPresentTimestamp) {
            subscriptionItem.noObserversPresentTimestamp = Date.now();
            return false;
          } else {
            return subscriptionItem.noObserversPresentTimestamp + this.deleteSubjectWithoutSubscriptAfter * 1000 < Date.now();
          }
        } else {
          delete subscriptionItem.noObserversPresentTimestamp;
          return false;
        }
      });
      this.unsubscribeMulti(isinsToUnsubscribe);
    });
  }

  public subscribeMulti(isins: string[]): Observable<Quote> {
    const quoteObservables = isins.map((isin) => this.subscribe(isin));
    return merge(...quoteObservables);
  }

  public subscribeMultiBuffered(isins: string[], extreme?: boolean): Observable<Record<string, Quote>> {
    const quoteObservables = isins.map((isin) => this.subscribe(isin));
    return merge(...quoteObservables).pipe(
      buffer(PushQuoteService.createQuoteBufferFn(isins.length, extreme)),
      map((quoteArr) =>
        quoteArr.reduce<Record<string, Quote>>((resultMap, quote) => {
          if (quote) {
            resultMap[quote.isin] = quote;
          }
          return resultMap;
        }, {}),
      ),
    );
  }

  public subscribe(isin: string): Observable<Quote> {
    // whatever it takes, poll the first quote even after navigation only
    this.initialPollingTrigger$.next();

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      // Book keeping
      delete this.isins[isin].noObserversPresentTimestamp;
      return this.isins[isin].quoteSubject;
    }

    // Book keeping
    const quoteSubject = new BehaviorSubject<Quote>(null);
    this.isins[isin] = { quoteSubject: quoteSubject };

    // Notify to subscribe isin
    this.isinsToSubscribe.next(isin);
    return quoteSubject.asObservable();
  }

  public unsubscribeMulti(isins: string[]): void {
    isins?.length && isins.forEach((isin) => this.unsubscribe(isin));
  }

  public unsubscribe(isin?: string): void {
    if (!isin) {
      return;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      // Book keeping
      this.isins[isin].quoteSubject.complete();
      delete this.isins[isin];

      // Notify to unsubscribe isin
      this.isinsToUnsubscribe.next(isin);
    }
  }

  protected onValueFromDiffusion(topic: string, spec: TopicSpecification, newValue: unknown): void {
    const isin = topic.split('/')[1];

    // eslint-disable-next-line no-prototype-builtins
    if (this.isins.hasOwnProperty(isin)) {
      const newQuote = PushQuoteService.createQuoteFromDiffusionValue(newValue, isin);

      // notify subscribers about new quote
      this.isins[isin].quoteSubject.next(newQuote);
    }
  }

  private subscribeFromDiffusion(diffusionSession: Session, isin: string): void {
    if (diffusionSession.isConnected()) {
      diffusionSession.select(`wp/${isin}`).then(
        () => {
          // nothing to do
        },
        () => {
          // TODO: error handling
        },
      );
    }
  }

  private unsubscribeFromDiffusion(diffusionSession: Session, isin: string): void {
    if (diffusionSession.isConnected()) {
      diffusionSession.unsubscribe(`wp/${isin}`).then(
        () => {
          // nothing to do
        },
        () => {
          // TODO: error handling
        },
      );
    }
  }

  private pollQuotes(): void {
    const isinsToPoll: string[] = [];
    Object.keys(this.isins).forEach((isin) => isinsToPoll.push(isin));
    isinsToPoll.length && this.doQuoteRequestViaPOST(isinsToPoll);
  }

  private pollQuote(isin: string): void {
    const quotesUrl = `${environment.apihost}/api/quote?isins=${isin}`;
    this.doQuoteRequest(quotesUrl);
  }

  private doQuoteRequest(quotesUrl): void {
    firstValueFrom(this.httpClient.get(quotesUrl))
      .then((quotesResult) => {
        Object.keys(quotesResult).forEach((resultIsin) => {
          const isinSubject = this.isins[resultIsin];
          if (isinSubject?.quoteSubject) {
            isinSubject.quoteSubject.next(PushQuoteService.createQuoteFromJsonValue(quotesResult[resultIsin], resultIsin));
          }
        });
      })
      .catch(() => {
        // TODO: error handling
      });
  }

  private doQuoteRequestViaPOST(isins: string[]): void {
    const quotesUrl = `${environment.apihost}/api/quote`;
    const request = this.authService.isLoggedIn$().pipe(
      filter((isLoggedIn) => isLoggedIn),
      switchMap(() => this.httpClient.post(quotesUrl, isins).pipe(retry(2))),
    );
    firstValueFrom(request)
      .then((quotesResult) => {
        Object.keys(quotesResult).forEach((resultIsin) => {
          const isinSubject = this.isins[resultIsin];
          if (isinSubject?.quoteSubject) {
            isinSubject.quoteSubject.next(PushQuoteService.createQuoteFromJsonValue(quotesResult[resultIsin], resultIsin));
          }
        });
      })
      .catch(() => {
        // TODO: error handling
      });
  }
}
